import {Component, HostListener} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
    faAngleLeft,
    faAngleRight,
    faFileExcel,
    faPen,
    faSquarePlus,
    faSquareXmark,
    faTrash,
    faUpload
} from '@fortawesome/free-solid-svg-icons';
import {ApiService} from '../services/api.service';
import {ToastrService} from 'ngx-toastr';
import {getFileNameFromResponse} from '../../common/utils';
import saveAs from 'file-saver';
import {GenericSearchComponent} from '../shared/search-suggestion/search-suggestion.component';
import {VehicleNumberCopyDirective} from '../shared/vehicle-number-copy/vehicle-number-copy.directive';
import {BulkImportResultComponent} from '../shared/bulk-import-result/bulk-import-result.component';
import {
    BulkImportResultView,
    handleBulkImportError,
} from '../shared/bulk-import-result/bulk-import-result.utils';
import {BulkUploadSupportedValuesComponent} from '../shared/components/bulk-upload-supported-values/bulk-upload-supported-values.component';
import {MonitoringTypeBadgeComponent} from '../shared/monitoring-type-badge/monitoring-type-badge.component';
import {SeverityBadgeComponent} from '../shared/severity-badge/severity-badge.component';
import {MonitoringTypeMultiselectComponent} from '../shared/monitoring-type-multiselect/monitoring-type-multiselect.component';
import {
    VEHICLE_MONITORING_BULK_UPLOAD_SUPPORTED_FIELDS,
    VEHICLE_MONITORING_BULK_UPLOAD_SUPPORTED_TIP,
    VEHICLE_MONITORING_SEVERITY_OPTIONS,
} from '../shared/vehicle-monitoring/vehicle-monitoring.constants';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../shared/constants/pagination.constants';

export type GroupedMonitoringRule = {
    id: number;
    type: string;
    type_display: string;
    severity: string;
    severity_display: string;
    remarks: string;
};

export type GroupedVehicleMonitoring = {
    id: number;
    vehicle_number: string;
    monitoring_rules: GroupedMonitoringRule[];
    highest_severity: string;
    highest_severity_display: string;
    remarks: string;
    created_by: string;
    created_on: string;
};

@Component({
    selector: 'app-vehicle-monitoring',
    imports: [
        CommonModule,
        FormsModule,
        FontAwesomeModule,
        GenericSearchComponent,
        VehicleNumberCopyDirective,
        BulkImportResultComponent,
        BulkUploadSupportedValuesComponent,
        MonitoringTypeBadgeComponent,
        SeverityBadgeComponent,
        MonitoringTypeMultiselectComponent,
    ],
    templateUrl: './vehicle-monitoring.component.html',
    styleUrl: './vehicle-monitoring.component.css'
})
export class VehicleMonitoringComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
    searchText: string | null = '';
    selectedAlertTypes: string[] = [];
    selectedSeverities: string[] = [];
    severityFilterOptions = VEHICLE_MONITORING_SEVERITY_OPTIONS;
    severityFilterOrder = VEHICLE_MONITORING_SEVERITY_OPTIONS.map(option => option.value);
    recordsPerPage: number = 10;
    currentPage: number = 1;
    vehicles: GroupedVehicleMonitoring[] = [];
    nextPage: number | null = null;
    previousPage: number | null = null;
    showDeleteDialog = false;
    selectedVehicle: GroupedVehicleMonitoring | null = null;
    selectedRuleIds = new Set<number>();
    deleting = false;
    totalRecords = 0;
    totalMonitoringRules = 0;
    role = '';
    todayDate: Date = new Date();
    hoverRowKey: string | null = null;
    bulkImportResult: BulkImportResultView | null = null;
    showBulkUploadDialog = false;
    bulkUploadFile: File | null = null;
    uploadingBulk = false;

    bulkUploadSupportedFields = VEHICLE_MONITORING_BULK_UPLOAD_SUPPORTED_FIELDS;
    bulkUploadSupportedTip = VEHICLE_MONITORING_BULK_UPLOAD_SUPPORTED_TIP;

    constructor(
        private apiService: ApiService,
        library: FaIconLibrary,
        private router: Router,
        private toastr: ToastrService,
    ) {
        this.fetchData(this.currentPage, this.recordsPerPage);
        library.addIcons(faFileExcel, faAngleRight, faAngleLeft, faUpload, faSquarePlus, faSquareXmark, faPen, faTrash);

        const user = localStorage.getItem('user');
        if (user) {
            const userObj = JSON.parse(user);
            this.role = userObj['groups'];
        }
    }

    get searchExtraBody(): Record<string, string> {
        const body: Record<string, string> = {};
        if (this.selectedAlertTypes.length) {
            body['alert_types'] = this.selectedAlertTypes.join(',');
        }
        if (this.selectedSeverities.length) {
            body['severities'] = this.selectedSeverities.join(',');
        }
        return body;
    }

    fetchData(page: number, pageSize: number) {
        this.apiService.getGroupedVehicleMonitoring(
            page,
            pageSize,
            this.searchText,
            this.selectedAlertTypes,
            this.selectedSeverities,
        ).subscribe({
            next: (data: any) => {
                this.vehicles = data['results'];
                this.totalRecords = data['count'];
                this.totalMonitoringRules = data['total_monitoring_rules'] ?? 0;
                this.nextPage = data['next'];
                this.previousPage = data['previous'];
            }
        });
    }

    get totalPages(): number {
        let totalPage = Math.ceil(this.totalRecords / this.recordsPerPage);
        if (totalPage === 0) {
            totalPage = 1;
        }
        return totalPage;
    }

    next() {
        if (this.nextPage) {
            this.currentPage++;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    previous() {
        if (this.previousPage) {
            this.currentPage--;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    selectPageSize() {
        if (this.recordsPerPage) {
            this.currentPage = 1;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    applyAlertTypeFilter(selectedValues: string[]) {
        this.selectedAlertTypes = selectedValues;
        this.currentPage = 1;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    applySeverityFilter(selectedValues: string[]) {
        this.selectedSeverities = selectedValues;
        this.currentPage = 1;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    openDeleteDialog(vehicle: GroupedVehicleMonitoring) {
        this.selectedVehicle = vehicle;
        this.selectedRuleIds = new Set(vehicle.monitoring_rules.map(rule => rule.id));
        this.showDeleteDialog = true;
    }

    closeDeleteDialog() {
        if (this.deleting) {
            return;
        }
        this.showDeleteDialog = false;
        this.selectedVehicle = null;
        this.selectedRuleIds.clear();
    }

    isRuleSelected(ruleId: number): boolean {
        return this.selectedRuleIds.has(ruleId);
    }

    toggleRuleSelection(ruleId: number, checked: boolean) {
        if (checked) {
            this.selectedRuleIds.add(ruleId);
            return;
        }
        this.selectedRuleIds.delete(ruleId);
    }

    editVehicle(id: number) {
        this.router.navigate(['/edit-vehicle-monitoring', id]);
    }

    deleteSelectedRules() {
        if (!this.selectedVehicle || this.selectedRuleIds.size === 0 || this.deleting) {
            return;
        }

        this.deleting = true;
        this.apiService.deleteVehicleMonitoringRules(Array.from(this.selectedRuleIds)).subscribe({
            next: () => {
                this.deleting = false;
                this.toastr.success('Selected monitoring types removed successfully.', 'Success');
                this.closeDeleteDialog();
                this.fetchData(this.currentPage, this.recordsPerPage);
            },
            error: (err: any) => {
                this.deleting = false;
                this.handleError(err);
            },
        });
    }

    exportToExcel() {
        this.apiService.exportVehicleMonitoringRules(
            this.searchText,
            this.selectedAlertTypes,
            this.selectedSeverities,
        ).subscribe({
            next: (response: any) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err: any) => this.toastr.error(`${err}`, 'Error'),
        });
    }

    addVehicle() {
        this.router.navigate(['/add-vehicle-monitoring']);
    }

    downloadTemplate() {
        this.apiService.downloadImportTemplate('vehicle_alert_rules').subscribe({
            next: (response: any) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err: any) => this.handleError(err),
        });
    }

    openBulkUploadDialog() {
        this.bulkUploadFile = null;
        this.uploadingBulk = false;
        this.showBulkUploadDialog = true;
    }

    closeBulkUploadDialog() {
        if (this.uploadingBulk) {
            return;
        }
        this.showBulkUploadDialog = false;
        this.bulkUploadFile = null;
    }

    onBulkFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        this.bulkUploadFile = input.files?.[0] ?? null;
    }

    submitBulkUpload() {
        if (!this.bulkUploadFile || this.uploadingBulk) {
            return;
        }

        this.bulkImportResult = null;
        this.uploadingBulk = true;
        const formData = new FormData();
        formData.append('file', this.bulkUploadFile);
        this.apiService.bulkUploadVehicleAlertRules(formData).subscribe({
            next: (response: any) => {
                this.uploadingBulk = false;
                this.bulkImportResult = null;
                this.showBulkUploadDialog = false;
                this.bulkUploadFile = null;
                this.toastr.success(response?.message || 'Monitoring records uploaded successfully.', 'Success');
                this.fetchData(this.currentPage, this.recordsPerPage);
            },
            error: (err: any) => {
                this.uploadingBulk = false;
                this.bulkImportResult = handleBulkImportError(
                    err,
                    this.toastr,
                    'vehicle_alert_rules_bulk_upload_failures.csv',
                );
                this.showBulkUploadDialog = false;
                this.bulkUploadFile = null;
            },
        });
    }

    onResults(event: {
        searchText: string;
        results: any[];
        totalRecords: number;
        totalMonitoringRules?: number;
        nextPage: number | null;
        previousPage: number | null;
    }) {
        this.currentPage = 1;
        this.recordsPerPage = 10;
        this.previousPage = event.previousPage;
        this.nextPage = event.nextPage;
        this.searchText = event.searchText;
        this.vehicles = event.results;
        this.totalRecords = event.totalRecords;
        this.totalMonitoringRules = event.totalMonitoringRules ?? 0;
    }

    private handleError(err: any) {
        if (err?.error?.detail) {
            this.toastr.error(err.error.detail, 'Error');
        } else if (err?.error?.alert_type) {
            err.error.alert_type.forEach((message: string) => this.toastr.error(message, 'Error'));
        } else if (err?.status !== 403 && err.status !== 0) {
            this.toastr.error('Something went wrong!', 'Error');
        }
        this.closeDeleteDialog();
    }

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event) {
        const target = event.target as HTMLElement;
        const modalElement = document.querySelector('.delete-monitoring-modal .modal-content');
        if (modalElement && !modalElement.contains(target) && this.showDeleteDialog && !this.deleting) {
            this.closeDeleteDialog();
        }
    }
}
